import { MODALS, OVERDUE_STATUSES, OverdueStatus, WELLNESS_PLAN_STATUSES } from "../../config/params";
/* eslint-disable max-len */
/* eslint-disable quotes */
import React, { useEffect } from "react";

import AvatarCustom from "../AvatarCustom/AvatarCustom";
import { ISquareInvoices } from "../../services/delinquent-payment/delinquent-payment.interface";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import { PAGES_PATH } from "../../navigation/params";
import { SCRATCHPAY_ACCOUNT_URL } from "../../config/params";
import { StyledBookingFlowButton } from
  "../../shared/containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { fromUnixToFormattedVisitdateStr } from "../../utils/unix-timestamp";
import { getPlanOverdueText } from "../WellnessMemberPlanCard/WellnessMemberPlanCardOld";
import { useModal } from "../../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { userActivityLogger } from "../../utils/mixpanel";

export const getOverdueCtaText = (overdueStatus: string) => {
  switch (overdueStatus) {
  case OVERDUE_STATUSES.OVERDUE:
  case OVERDUE_STATUSES.PAUSED:
    return "Pay Now";
  case OVERDUE_STATUSES.CANCELLED:
    return "Contact Hospital";
  default:
    return "";
  }
};

export const handleOverdueCtaClick = (
  overdueStatus: OverdueStatus, petName: string, referredPage:string, pendingInvoices: ISquareInvoices[],
  isScratchpay: boolean, paymentUrl?: string, navigate?: ReturnType<typeof useNavigate>,
  openModal?: (modalName: string) => void, petId?: string, planName?: string ) => {
  const effectivePaymentUrl = isScratchpay ? SCRATCHPAY_ACCOUNT_URL : paymentUrl;
  if (isScratchpay && (overdueStatus === OVERDUE_STATUSES.OVERDUE || overdueStatus === OVERDUE_STATUSES.PAUSED)) {
    userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_CTA_CLICKED, {
      provider: "scratchpay",
      delinquency_state: overdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue_notice" : "paused",
      entry_point: referredPage === "dashboard" ? "dashboard" : "care_portal",
      surface: "card",
      cta_type: "pay_now",
      pet_id: petId,
      pet_name: petName,
      plan_name: planName,
      is_scratchpay: true,
      payment_processor: "scratchpay",
      card_type: overdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue" : "locked",
      cta_action: "pay_now",
      has_payment_url: true,
      payment_url_source: "scratchpay_account_login",
      referred_from_page: referredPage,
    });
  }
  switch (overdueStatus) {
  case OVERDUE_STATUSES.CANCELLED:
    userActivityLogger(MIXPANEL_EVENTS.CANCELED_WP_USERS_CONTACT_HOSPITAL,
      {
        pet_name: petName,
        referred_from_page: referredPage,
      });
    openModal && openModal(MODALS.OVERDUE_CONTACT_MODAL);
    break;
  default:
    userActivityLogger(MIXPANEL_EVENTS.VIEW_OVERDUE_INVOICE_CLICKED,
      {
        pet_name: petName,
        referred_from_page: referredPage,
        no_of_overdue_invoices: pendingInvoices.length,
        is_scratchpay: isScratchpay,
      });
    if (!effectivePaymentUrl) {
      navigate && navigate(`${PAGES_PATH.USER_PROFILE}/${PAGES_PATH.USER_BILLING}`);
      return;
    }
    window.open(effectivePaymentUrl, "_blank", "noopener,noreferrer");
    break;
  }
};


interface IOutstandingPaymentCard{
  planOverdueStatus: OverdueStatus | null;
  petId?: string;
  petName: string;
  paymentStatus: string;
  pendingInvoices: ISquareInvoices[];
  paymentUrl: string;
  planEndDate: string;
  petImage?: string;
  isRenewUnsuccessful?: boolean;
  isCancelledRenewUnsuccessful?: boolean;
  isPlanEndingInFiveDays?: boolean;
  isScratchpay?: boolean;
}

const OutstandingPaymentCard: React.FC<IOutstandingPaymentCard> =
({ planOverdueStatus, petId, petName, petImage, paymentUrl, planEndDate,
  pendingInvoices, paymentStatus,
  isRenewUnsuccessful = false,
  isCancelledRenewUnsuccessful = false,
  isPlanEndingInFiveDays = false,
  isScratchpay = false }) => {
  const navigate=useNavigate();
  const { openModal } = useModal();
  const isScratchPayPaymentOverdue = isScratchpay && planOverdueStatus === OVERDUE_STATUSES.OVERDUE;
  const shouldShowWellnessBadge =
    planOverdueStatus === OVERDUE_STATUSES.PAUSED ||
    (isScratchpay && planOverdueStatus === OVERDUE_STATUSES.OVERDUE);
  const bannerClassName = (() => {
    if (isScratchPayPaymentOverdue) return "text-red-4 bg-red-3";
    if (
      planOverdueStatus === OVERDUE_STATUSES.OVERDUE ||
      planOverdueStatus === OVERDUE_STATUSES.PAUSED ||
      planOverdueStatus === OVERDUE_STATUSES.CANCELLED ||
      isRenewUnsuccessful ||
      isCancelledRenewUnsuccessful
    ) {
      return "text-white bg-red-4";
    }
    return "text-red-4 bg-red-3";
  })();

  const getOverdueSubtext=(overdueStatus: OverdueStatus, petName: string)=>{
    switch (overdueStatus) {
    case OVERDUE_STATUSES.OVERDUE:
      return "Your ScratchPay payment could not be paid. Please login to your ScratchPay account to bring your account to good standing.";
    case OVERDUE_STATUSES.PAUSED:
      if (isScratchpay && !isPlanEndingInFiveDays && !isRenewUnsuccessful) {
        return "You have two overdue payments on your TotalCare plan. Please login to your ScratchPay account to bring your account to good standing. Completing payment will unpause your benefits.";
      }
      return isPlanEndingInFiveDays?
        `Please pay before ${fromUnixToFormattedVisitdateStr(+planEndDate)} —after that, we won’t be able to renew your plan.`:
        isRenewUnsuccessful?
          ` ${petName}’s Wellness Plan was not renewed due to an unpaid balance.
        To continue booking appointments  please pay the outstanding invoice.`:
          `We could not process your wellness plan payment. 
      Please pay your invoice and update your payment method to reinstate your benefits.`;
    case OVERDUE_STATUSES.CANCELLED:
      return isCancelledRenewUnsuccessful?
        `Payment failed when trying to renew your plan. Update your payment method today to renew your plan.`:
        `Please contact your GoodVets hospital to settle your account before booking any future services.`;
    default:
      return "";
    }
  };
  const getOverdueHeading=(overdueStatus: OverdueStatus, petName: string)=>{
    switch (overdueStatus) {
    case OVERDUE_STATUSES.OVERDUE:
      return `${petName} Wellness Plan Payment is Overdue`;
    case OVERDUE_STATUSES.PAUSED:
      if (isScratchpay && !isPlanEndingInFiveDays && !isRenewUnsuccessful) {
        return `${petName}’s TotalCare Plan Has Been Paused`;
      }
      return isPlanEndingInFiveDays?
        `${petName} has an unpaid GoodPet+ payment`:
        isRenewUnsuccessful?
          `${petName}’s  Wellness Plan renewal failed`:
          ` ${petName}’s Wellness Plan Paused`;
    case OVERDUE_STATUSES.CANCELLED:
      return isCancelledRenewUnsuccessful?
        `${petName}’s Wellness Plan renewal failed`:
        `${petName}’s GoodPet+ Plan Has Been Canceled`;
    default:
      return "";
    }
  };

  useEffect(()=>{
    userActivityLogger(MIXPANEL_EVENTS.DASHBOARD_OVERDUE_CARD_SHOWN,
      {
        pet_name: petName,
        no_of_overdue_invoices: pendingInvoices.length,
        card_type: paymentStatus === WELLNESS_PLAN_STATUSES.PAUSED ? WELLNESS_PLAN_STATUSES.LOCKED : paymentStatus,
      });
    if (isScratchpay && (planOverdueStatus === OVERDUE_STATUSES.OVERDUE || planOverdueStatus === OVERDUE_STATUSES.PAUSED)) {
      userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_SURFACE_SHOWN, {
        provider: "scratchpay",
        delinquency_state: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue_notice" : "paused",
        entry_point: "dashboard",
        surface: "card",
        pet_id: petId,
        pet_name: petName,
        is_scratchpay: true,
        payment_processor: "scratchpay",
        card_type: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue" : "locked",
        has_payment_url: true,
        payment_url_source: "scratchpay_account_login",
        referred_from_page: "dashboard",
      });
    }
  }
  , []);


  return (
    <>
      {planOverdueStatus &&
      <div className="bg-gray-3 px-4 py-6">
        <div className="rounded-xl bg-white flex flex-col p-4 pt-3 gap-4 shadow-lg">

          <div className={`text-center text-sm font-semibold
          ${bannerClassName} rounded py-0.5 mb-[-0.2rem]`}>
            {getPlanOverdueText(planOverdueStatus, isRenewUnsuccessful, isCancelledRenewUnsuccessful, isPlanEndingInFiveDays)}
          </div>
          <div className="flex gap-3 w-full items-center">
            <div>
              <AvatarCustom
                width={70}
                height={70}
                image={petImage}
                name={petName}
                isWellnessMember={shouldShowWellnessBadge}
                isPlanOverdue={planOverdueStatus !== null && (planOverdueStatus === OVERDUE_STATUSES.CANCELLED ||
                  isRenewUnsuccessful || isCancelledRenewUnsuccessful)}
              />
            </div>
            <div>
              <div className="font-semibold">
                <div>
                  {getOverdueHeading(planOverdueStatus, petName)}
                </div>
              </div>
              <div className="text-sm text-justify">
                {getOverdueSubtext(planOverdueStatus, petName)}
              </div>
            </div>
          </div>
          <div>
            <StyledBookingFlowButton
              disabled={false}
              fullWidth
              color="primary"
              size="small"
              onClick={()=>handleOverdueCtaClick(planOverdueStatus, petName, "dashboard", pendingInvoices, isScratchpay,
                paymentUrl, navigate, openModal, petId)}
            >
              {getOverdueCtaText(planOverdueStatus)}
            </StyledBookingFlowButton>
          </div>
        </div>
      </div>}
    </>
  );
};

export default OutstandingPaymentCard;
